import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@/data/api";
import { trpc } from "@/trpc";
import { queryKeys } from "@/queries/keys";

/** Data: SitePageResponse[] */
export function useWebsitePagesOptional() {
  return trpc.admin.website.listPages.useQuery();
}

/** Data: SiteSettingsResponse */
export function useWebsiteSettingsOptional() {
  return trpc.admin.website.getSettings.useQuery();
}

export function useWebsiteMenusOptional() {
  return trpc.admin.website.getMenus.useQuery();
}

/** Data: SiteAnnouncementResponse */
export function useWebsiteAnnouncementOptional() {
  return trpc.admin.website.getAnnouncement.useQuery();
}

export function useWebsiteUpdateAnnouncement() {
  const utils = trpc.useUtils();
  return trpc.admin.website.updateAnnouncement.useMutation({
    onSuccess: () => {
      void utils.admin.website.getAnnouncement.invalidate();
    },
  });
}

export function useWebsiteContactSubmissions(params: { page: number; limit: number }) {
  return trpc.admin.website.listContactSubmissions.useQuery(params, {
    placeholderData: keepPreviousData,
  });
}

export function useWebsiteContactMemberSubmissions(params: { page: number; limit: number }) {
  return trpc.admin.website.listContactMemberSubmissions.useQuery(params, {
    placeholderData: keepPreviousData,
  });
}

export function useWebsiteFeedbackSubmissions() {
  const api = useApi();
  return useQuery({
    queryKey: ["website", "feedback-submissions"],
    queryFn: () => api.website.listFeedbackSubmissions(),
  });
}

export function useWebsiteBlogAll() {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.websiteBlogAll,
    queryFn: () => api.website.listBlogAll(),
  });
}

export function useWebsiteCreateBlogPost() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => api.website.createBlogPost(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.websiteBlogAll }),
  });
}

export function useWebsiteUpdateBlogPost() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      api.website.updateBlogPost(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.websiteBlogAll }),
  });
}

export function useWebsiteDeleteBlogPost() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.website.deleteBlogPost(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.websiteBlogAll }),
  });
}

export function useWebsiteUploadBlogInlineImage() {
  const api = useApi();
  return useMutation({
    mutationFn: (image: string) => api.website.uploadBlogInlineImage(image),
  });
}

export function useWebsiteMembersFeed() {
  const api = useApi();
  return useQuery({
    queryKey: ["website", "members-feed"],
    queryFn: () => api.website.getMembersFeed(),
  });
}

export function useWebsiteEventsFeed() {
  return trpc.website.getEventsFeed.useQuery();
}

export function useWebsiteCreatePage() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => api.website.createPage(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.websitePages }),
  });
}

export function useWebsiteUpdatePage() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      api.website.updatePage(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.websitePages }),
  });
}

export function useWebsiteDeletePage() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.website.deletePage(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.websitePages }),
  });
}

export function useWebsiteUpdateSettings() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => api.website.updateSettings(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.websiteSettings }),
  });
}

export function useWebsiteUpdateMenu() {
  const api = useApi();
  const utils = trpc.useUtils();
  return useMutation({
    mutationFn: ({
      key,
      items,
    }: {
      key: string;
      items: {
        label: string;
        url?: string | null;
        internal_ref?: string | null;
        sort_order?: number;
      }[];
    }) => api.website.updateMenu(key, items),
    onSuccess: () => {
      void utils.admin.website.getMenus.invalidate();
    },
  });
}
